import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V2_BASE_URL } from '../../config/environment';
import {
  Layout,
  Card,
  Typography,
  Button,
  Row,
  Col,
  Tag,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Progress,
  Alert,
  Space,
  Statistic,
  message,
  Popconfirm,
  Upload,
  Select,
  Spin,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  SettingOutlined,
  KeyOutlined,
  BarChartOutlined,
  CodeOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  ApiOutlined,
  ArrowLeftOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  UploadOutlined,
  CopyOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { applicationAPI, documentTypesAPI, appOcrAPI } from '../../services/api';
import AppHeader from '../../components/Header';
import LoadingAnimation from '../../components/LoadingAnimation';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface Application {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'suspended';
  quota_limit: number;
  quota_used: number;
  current_usage: number;
  rate_limit_per_minute: number;
  total_requests: number;
  created_at: string;
  quota_info: {
    available: boolean;
    quota_remaining: number;
    quota_used: number;
    quota_limit: number;
  };
  api_keys?: any[];
}

interface QuotaInfo {
  total_quota: number;
  used_quota: number;
  remaining_quota: number;
  usage_percentage: number;
}

const APIManagement: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [companyQuota, setCompanyQuota] = useState<QuotaInfo | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [createAppModal, setCreateAppModal] = useState(false);
  const [testEndpointModal, setTestEndpointModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  
  // Document types state
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [curlCommand, setCurlCommand] = useState('');
  const loadingRef = useRef({ data: false, documentTypes: false });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (loadingRef.current.data) return;
    
    try {
      loadingRef.current.data = true;
      setLoading(true);
      const [appsResponse, quotaResponse] = await Promise.all([
        applicationAPI.getApplications(),
        applicationAPI.getCompanyQuota()
      ]);
      
      console.log('📊 Applications data received:', appsResponse.data.data);
      setApplications(appsResponse.data.data);
      setCompanyQuota(quotaResponse.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Failed to load application data');
    } finally {
      setLoading(false);
      loadingRef.current.data = false;
    }
  };

  const loadDocumentTypes = async () => {
    if (loadingRef.current.documentTypes) return;
    
    try {
      loadingRef.current.documentTypes = true;
      setDocumentTypesLoading(true);
      
      // First, try to use the ultra-fast cached endpoint with the current API key
      if (selectedApp?.api_keys && selectedApp.api_keys.length > 0) {
        const apiKey = selectedApp.api_keys[0].key; // Use first API key
        console.log('🚀 Loading document types from REDIS CACHE...');
        const startTime = performance.now();
        
        try {
          const response = await appOcrAPI.getDocumentTypesCached(apiKey);
          const loadTime = performance.now() - startTime;
          
          console.log(`✅ Document types loaded from Redis in ${loadTime.toFixed(1)}ms`);
          setDocumentTypes(response.data.data || []);
          return; // Success - exit early
        } catch (cacheError) {
          console.warn('⚠️ Redis cache failed, falling back to database:', cacheError);
        }
      }
      
      // Fallback to database if Redis cache fails or no API key
      console.log('⚠️ Loading document types from DATABASE (slow)...');
      const startTime = performance.now();
      const response = await documentTypesAPI.getDocumentTypes(true); // Get only active document types
      const loadTime = performance.now() - startTime;
      
      console.log(`⚠️ Document types loaded from database in ${loadTime.toFixed(1)}ms`);
      setDocumentTypes(response.data.data || []);
      
    } catch (error) {
      console.error('Error loading document types:', error);
      message.error('Failed to load document types');
    } finally {
      setDocumentTypesLoading(false);
      loadingRef.current.documentTypes = false;
    }
  };

  const generateCurlCommand = (apiKey: string, documentType?: string, storeImage: boolean = false) => {
    const baseUrl = API_V2_BASE_URL;
    const curl = [
      'curl -X POST \\',
      `  '${baseUrl}/v2/app-ocr/process' \\`,
      `  -H 'Content-Type: multipart/form-data' \\`,
      `  -F 'api_key=${apiKey}' \\`,
      `  -F 'image=@/path/to/your/image.jpg' \\`,
      `  -F 'store_image=${storeImage}'`
    ];
    
    if (documentType) {
      curl.splice(-1, 0, `  -F 'document_type=${documentType}' \\`);
    }
    
    // Remove the last backslash from the last line
    const lastIndex = curl.length - 1;
    curl[lastIndex] = curl[lastIndex].replace(' \\', '');
    
    return curl.join('\n');
  };

  const handleCreateApplication = async (values: any) => {
    try {
      await applicationAPI.createApplication(values);
      message.success('Application created successfully');
      setCreateAppModal(false);
      form.resetFields();
      loadData();
    } catch (error) {
      console.error('Error creating application:', error);
      message.error('Failed to create application');
    }
  };

  const handleNetworkTest = async () => {
    console.log('🔍 TESTING NETWORK CONNECTION...');
    const startTime = performance.now();
    
    try {
      const apiV2BaseUrl = API_V2_BASE_URL;
      const response = await fetch(`${apiV2BaseUrl}/v2/app-ocr/ping`, {
        method: 'GET',
      });
      
      const result = await response.json();
      const totalTime = performance.now() - startTime;
      
      console.log(`🔍 NETWORK TEST COMPLETE: ${totalTime.toFixed(2)}ms`);
      console.log('📊 Backend Response:', result);
      
      if (totalTime < 100) {
        message.success(`Network is excellent! Response in ${totalTime.toFixed(0)}ms`);
      } else if (totalTime < 1000) {
        message.success(`Network is good: ${totalTime.toFixed(0)}ms response time`);
      } else {
        message.warning(`Network is slow: ${totalTime.toFixed(0)}ms response time`);
      }
      
    } catch (error: any) {
      console.error('🚨 NETWORK TEST FAILED:', error);
      message.error('Network test failed. Check if backend is running on port 8001.');
    }
  };

  const handleTestEndpoint = async (values: any) => {
    const startTime = performance.now();
    console.log('🚀 FRONTEND TEST STARTED at:', new Date().toISOString());
    
    if (!values.api_key) {
      message.error('Please enter your API key');
      return;
    }

    // Validate API key format
    if (!values.api_key.startsWith('ocr_api_')) {
      message.warning('API key should start with "ocr_api_". Please verify you have the correct key.');
    }

    try {
      const loadingStartTime = performance.now();
      setTestLoading(true);
      setTestResult(null);
      console.log(`⏱️ Set loading state: ${(performance.now() - startTime).toFixed(2)}ms`);

      const formDataStartTime = performance.now();
      const formData = new FormData();
      formData.append('api_key', values.api_key);
      
      // Debug logging
      console.log('Testing with API key:', values.api_key);
      console.log('API key length:', values.api_key.length);
      console.log('API key starts with ocr_api_:', values.api_key.startsWith('ocr_api_'));
      
      if (values.image) {
        // Handle the uploaded file
        const fileProcessStartTime = performance.now();
        const file = values.image.originFileObj || values.image.file || values.image;
        if (file) {
          formData.append('image', file);
          console.log(`⏱️ File processed: ${(performance.now() - fileProcessStartTime).toFixed(2)}ms`);
        } else {
          message.error('Please select a valid image file');
          setTestLoading(false);
          return;
        }
      } else {
        // Create a simple test image if none provided
        const canvasStartTime = performance.now();
        const testText = 'Sample OCR Text for Testing';
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, 400, 200);
          ctx.fillStyle = '#000000';
          ctx.font = '24px Arial';
          ctx.fillText(testText, 50, 100);
        }
        
        // Convert to blob and append
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob!), 'image/png');
        });
        formData.append('image', blob, 'test.png');
        console.log(`⏱️ Canvas image created: ${(performance.now() - canvasStartTime).toFixed(2)}ms`);
      }

      if (values.document_type) {
        formData.append('document_type', values.document_type);
      }

      // Add store_image parameter based on user preference
      const storeImage = values.store_image || false;
      formData.append('store_image', storeImage.toString());
      
      console.log(`⏱️ FormData preparation complete: ${(performance.now() - formDataStartTime).toFixed(2)}ms`);

      // NETWORK REQUEST TIMING
      const networkStartTime = performance.now();
      console.log('🌐 STARTING NETWORK REQUEST...');
      
      const apiV2BaseUrl = API_V2_BASE_URL;
      const response = await fetch(`${apiV2BaseUrl}/v2/app-ocr/process`, {
        method: 'POST',
        body: formData,
        // Removed timeout and other constraints - let it be as fast as possible
      });
      
      const networkEndTime = performance.now();
      const networkTime = networkEndTime - networkStartTime;
      console.log(`🌐 NETWORK REQUEST COMPLETE: ${networkTime.toFixed(2)}ms`);

      const jsonParseStartTime = performance.now();
      const result = await response.json();
      const jsonParseTime = performance.now() - jsonParseStartTime;
      console.log(`📄 JSON Parse time: ${jsonParseTime.toFixed(2)}ms`);
      
      // LOG BACKEND PROCESSING TIME vs FRONTEND DELAY
      if (result.processing_time) {
        const backendTime = result.processing_time * 1000; // Convert to ms
        console.log(`🔥 BACKEND PROCESSING TIME: ${backendTime.toFixed(2)}ms`);
        console.log(`🌐 NETWORK ONLY TIME: ${networkTime.toFixed(2)}ms`);
        console.log(`⚠️ FRONTEND OVERHEAD: ${(networkTime - backendTime).toFixed(2)}ms`);
        
        if (networkTime - backendTime > 1000) {
          console.warn('🐌 SIGNIFICANT FRONTEND/NETWORK DELAY DETECTED!');
          console.warn('💡 Suggestions:');
          console.warn('   - Check if backend is running on correct port');
          console.warn('   - Verify no proxy/firewall blocking requests');
          console.warn('   - Consider using direct IP instead of localhost');
        }
      }
      
      const uiUpdateStartTime = performance.now();
      if (response.ok) {
        setTestResult({
          success: true,
          data: result,
          status: response.status,
          statusText: 'OK'
        });
        message.success('Endpoint test successful!');
        
        // Auto-refresh applications list to show updated quota usage
        console.log('🔄 Auto-refreshing applications after test...');
        setTimeout(() => {
          loadData();
        }, 500);  // Small delay to ensure backend has processed the update
        
      } else {
        setTestResult({
          success: false,
          error: result,
          status: response.status,
          statusText: response.statusText
        });
        message.error(`Test failed: ${result.detail || result.message || 'Unknown error'}`);
      }
      const uiUpdateTime = performance.now() - uiUpdateStartTime;
      console.log(`🎨 UI Update time: ${uiUpdateTime.toFixed(2)}ms`);
      
    } catch (error: any) {
      console.error('Test error:', error);
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        status: 0,
        statusText: 'Network Error'
      });
      message.error('Network error during test');
    } finally {
      const finalStartTime = performance.now();
      setTestLoading(false);
      const finalTime = performance.now() - finalStartTime;
      const totalTime = performance.now() - startTime;
      
      console.log(`⏱️ Set loading false time: ${finalTime.toFixed(2)}ms`);
      console.log(`🏁 TOTAL FRONTEND TIME: ${totalTime.toFixed(2)}ms`);
      console.log('✅ FRONTEND TEST COMPLETED at:', new Date().toISOString());
    }
  };

  const handleOpenTestModal = async (app: Application) => {
    const modalStartTime = performance.now();
    console.log('🎭 OPENING TEST MODAL for:', app.name);
    
    setSelectedApp(app);
    setTestResult(null);
    testForm.resetFields();
    console.log(`⏱️ Modal state reset: ${(performance.now() - modalStartTime).toFixed(2)}ms`);
    
    // Load document types and API keys in parallel
    const loadPromises = [];
    const loadStartTime = performance.now();
    
    // Load API keys if not already loaded
    if (!app.api_keys) {
      console.log('📚 Loading API keys for modal...');
      const loadApiKeys = applicationAPI.getApiKeys(app.id)
        .then(response => {
          const updatedApp = { ...app, api_keys: response.data.data };
          setSelectedApp(updatedApp);
          
          // Update the applications list with the loaded API keys
          setApplications(prev => 
            prev.map(prevApp => 
              prevApp.id === app.id ? updatedApp : prevApp
            )
          );
          console.log(`📚 API keys loaded: ${response.data.data.length} keys`);
        })
        .catch(error => {
          console.error('Error loading API keys:', error);
          message.error('Failed to load API keys for testing');
        });
      
      loadPromises.push(loadApiKeys);
    }
    
    // Load document types if not already loaded
    if (documentTypes.length === 0) {
      console.log('📄 Loading document types for modal...');
      loadPromises.push(loadDocumentTypes());
    }
    
    // Execute all load operations
    if (loadPromises.length > 0) {
      console.log(`⏳ Loading ${loadPromises.length} resources for modal...`);
      await Promise.all(loadPromises);
      const loadTime = performance.now() - loadStartTime;
      console.log(`⏱️ Modal resources loaded: ${loadTime.toFixed(2)}ms`);
    }
    
    setTestEndpointModal(true);
    const totalModalTime = performance.now() - modalStartTime;
    console.log(`🎭 MODAL OPENED SUCCESSFULLY: ${totalModalTime.toFixed(2)}ms total`);
  };

  const handleOpenSettings = (app: Application) => {
    setSelectedApp(app);
    // Pre-populate the form with current application data
    settingsForm.setFieldsValue({
      name: app.name,
      description: app.description,
      quota_limit: app.quota_limit,
      rate_limit_per_minute: app.rate_limit_per_minute
    });
    setSettingsModal(true);
  };

  const handleUpdateApplication = async (values: any) => {
    if (!selectedApp) return;
    
    try {
      await applicationAPI.updateApplication(selectedApp.id, values);
      message.success('Application updated successfully');
      setSettingsModal(false);
      await loadData(); // Reload the applications list
    } catch (error) {
      console.error('Error updating application:', error);
      message.error('Failed to update application');
    }
  };

  const handleStatusChange = (app: Application) => {
    setSelectedApp(app);
    setStatusModalVisible(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedApp) return;
    
    try {
      setStatusLoading(true);
      const newStatus = selectedApp.status === 'active' ? 'inactive' : 'active';
      
      await applicationAPI.updateApplicationStatus(selectedApp.id, newStatus);
      
      message.success(`Application ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setStatusModalVisible(false);
      await loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update application status');
    } finally {
      setStatusLoading(false);
    }
  };

  const applicationColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Application) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => navigate(`/api-management/applications/${record.id}`)}
            style={{ padding: 0 }}
          >
            {name}
          </Button>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : status === 'suspended' ? 'red' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Quota Usage',
      key: 'quota_usage',
      render: (_: any, record: Application) => {
        const current = record.current_usage || 0;
        const limit = record.quota_limit || 1;  // Avoid division by zero
        const percentage = Math.round((current / limit) * 100);
        
        return (
          <div style={{ width: '120px' }}>
            <Progress 
              percent={percentage} 
              size="small"
              status={percentage > 80 ? 'exception' : 'normal'}
            />
            <Text style={{ fontSize: '12px' }}>
              {current.toLocaleString()} / {record.quota_limit.toLocaleString()}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Total Requests',
      dataIndex: 'total_requests',
      key: 'total_requests',
      render: (count: number) => count?.toLocaleString() || 0,
    },
    {
      title: 'Rate Limit',
      dataIndex: 'rate_limit_per_minute',
      key: 'rate_limit_per_minute',
      render: (limit: number) => `${limit}/min`,
    },
    {
      title: 'API Keys',
      key: 'api_keys',
      render: (_: any, record: Application) => {
        const activeKeys = record.api_keys?.filter(k => k.status === 'active').length || 0;
        const totalKeys = record.api_keys?.length || 0;
        return `${activeKeys}/${totalKeys}`;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Application) => (
        <Space>
          <Button 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => navigate(`/api-management/applications/${record.id}`)}
          >
            View
          </Button>
          <Button 
            size="small" 
            icon={<PlayCircleOutlined />}
            onClick={() => handleOpenTestModal(record)}
          >
            Test
          </Button>
          <Button 
            size="small" 
            icon={<SettingOutlined />}
            onClick={() => handleOpenSettings(record)}
          >
            Settings
          </Button>
          <Button
            size="small"
            icon={record.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleStatusChange(record)}
            danger={record.status === 'active'}
            style={{ color: record.status === 'inactive' ? '#52c41a' : undefined }}
          >
            {record.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="layout-container">
      <AppHeader />
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>
        
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <ApiOutlined /> API Management
          </Title>
          <Paragraph>
            Manage your OCR API applications, monitor usage, and integrate with your systems.
          </Paragraph>
        </div>

        {/* Company Quota Overview */}
        {companyQuota && (
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Card title="Company Quota Overview">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="Total Quota" 
                      value={companyQuota.total_quota} 
                      formatter={(value) => value?.toLocaleString()}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Used Quota" 
                      value={companyQuota.used_quota} 
                      formatter={(value) => value?.toLocaleString()}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Remaining" 
                      value={companyQuota.remaining_quota} 
                      formatter={(value) => value?.toLocaleString()}
                    />
                  </Col>
                  <Col span={6}>
                    <div>
                      <Text>Usage</Text>
                      <Progress 
                        percent={companyQuota.usage_percentage} 
                        status={companyQuota.usage_percentage > 80 ? 'exception' : 'normal'}
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                  </Col>
                </Row>
                
                {companyQuota.usage_percentage > 90 && (
                  <Alert
                    message="High Usage Warning"
                    description="Your company quota usage is above 90%. Consider increasing your quota or optimizing usage."
                    type="warning"
                    showIcon
                    style={{ marginTop: '16px' }}
                  />
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Applications */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              title="Applications"
              extra={
                <Space>
                  <Button icon={<ReloadOutlined />} onClick={loadData}>
                    Refresh
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => setCreateAppModal(true)}
                  >
                    New Application
                  </Button>
                </Space>
              }
            >
              <Table
                columns={applicationColumns}
                dataSource={applications}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} of ${total} applications`,
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Create Application Modal */}
        <Modal
          title="Create New Application"
          open={createAppModal}
          onCancel={() => {
            setCreateAppModal(false);
            form.resetFields();
          }}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleCreateApplication}>
            <Form.Item
              name="name"
              label="Application Name"
              rules={[{ required: true, message: 'Please enter application name' }]}
            >
              <Input placeholder="e.g., Mobile App OCR" />
            </Form.Item>
            
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea rows={3} placeholder="Brief description of your application" />
            </Form.Item>
            
            <Form.Item
              name="quota_limit"
              label="Quota Limit"
              rules={[{ required: true, message: 'Please enter quota limit' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={companyQuota?.remaining_quota}
                placeholder="Number of requests"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => Number(value?.replace(/\$\s?|(,*)/g, ''))}
              />
            </Form.Item>
            
            <Form.Item
              name="rate_limit_per_minute"
              label="Rate Limit (requests per minute)"
              initialValue={60}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={1000}
                placeholder="60"
              />
            </Form.Item>
            
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setCreateAppModal(false)}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Create Application
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* Test Endpoint Modal */}
        <Modal
          title={`Test OCR Endpoint - ${selectedApp?.name}`}
          open={testEndpointModal}
          onCancel={() => {
            setTestEndpointModal(false);
            setTestResult(null);
            testForm.resetFields();
          }}
          footer={null}
          width={800}
        >
          <div style={{ marginBottom: '16px' }}>
            <Alert
              message="Test your OCR endpoint"
              description={
                <div>
                  Upload an image and enter your API key to test the OCR processing for application "{selectedApp?.name}". 
                  This will count towards your application's quota.
                </div>
              }
              type="info"
              showIcon
            />
          </div>

          {/* Network Test Section */}
          <div style={{ marginBottom: '16px' }}>
            <Button 
              onClick={handleNetworkTest}
              style={{ marginRight: '8px' }}
              icon={<ApiOutlined />}
            >
              Test Network Connection
            </Button>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Check if backend is responding quickly (should be &lt;100ms)
            </Text>
          </div>

          <Form form={testForm} layout="vertical" onFinish={handleTestEndpoint}>
            <Form.Item
              name="api_key"
              label="API Key"
              rules={[{ required: true, message: 'Please enter your API key' }]}
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Enter the API key for this application. You can find it in the application details page.
                </Text>
              }
            >
              <Input.Password 
                placeholder="Enter your API key" 
                visibilityToggle
                onChange={() => {
                  // Update curl command when API key changes
                  setTimeout(() => {
                    const apiKey = testForm.getFieldValue('api_key') || '[YOUR_API_KEY]';
                    const documentType = testForm.getFieldValue('document_type');
                    const storeImage = testForm.getFieldValue('store_image') || false;
                    const newCurlCommand = generateCurlCommand(apiKey, documentType, storeImage);
                    setCurlCommand(newCurlCommand);
                  }, 0);
                }}
              />
            </Form.Item>

            <Form.Item
              name="image"
              label="Upload Image"
              valuePropName="file"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.file;
              }}
            >
              <Upload
                name="image"
                listType="picture-card"
                maxCount={1}
                beforeUpload={() => false}
                accept="image/*"
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                }}
              >
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload Image</div>
                </div>
              </Upload>
            </Form.Item>

            <Form.Item
              name="document_type"
              label="Document Type (Optional)"
              extra={documentTypes.length === 0 ? (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  No custom document types found. Create document types in Document Management to see them here.
                </Text>
              ) : null}
            >
              <Select
                placeholder="Select document type"
                allowClear
                loading={documentTypesLoading}
                onChange={() => {
                  // Update curl command when document type changes
                  setTimeout(() => {
                    const apiKey = testForm.getFieldValue('api_key') || '[YOUR_API_KEY]';
                    const documentType = testForm.getFieldValue('document_type');
                    const storeImage = testForm.getFieldValue('store_image') || false;
                    const newCurlCommand = generateCurlCommand(apiKey, documentType, storeImage);
                    setCurlCommand(newCurlCommand);
                  }, 0);
                }}
                options={documentTypes.map(docType => ({
                  value: docType.document_type,
                  label: `${docType.document_type} - ${docType.description || 'No description'}`,
                }))}
                notFoundContent={documentTypesLoading ? <Spin size="small" /> : 'No document types found'}
              />
            </Form.Item>
            
            <Form.Item
              name="store_image"
              label="Store Image"
              valuePropName="checked"
              extra={
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Enable to store the uploaded image for future reference. Storage happens in the background and won't affect processing speed.
                </Text>
              }
            >
              <Switch 
                checkedChildren="Store" 
                unCheckedChildren="No Storage"
                defaultChecked={false}
                onChange={() => {
                  // Update curl command when store image toggle changes
                  setTimeout(() => {
                    const apiKey = testForm.getFieldValue('api_key') || '[YOUR_API_KEY]';
                    const documentType = testForm.getFieldValue('document_type');
                    const storeImage = testForm.getFieldValue('store_image') || false;
                    const newCurlCommand = generateCurlCommand(apiKey, documentType, storeImage);
                    setCurlCommand(newCurlCommand);
                  }, 0);
                }}
              />
            </Form.Item>
            
            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
              <Space>
                <Button onClick={() => setTestEndpointModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={testLoading}
                  icon={<PlayCircleOutlined />}
                >
                  Run Test
                </Button>
              </Space>
            </div>
          </Form>

          {/* Curl Command Example */}
          <Card 
            title="Equivalent cURL Command" 
            size="small" 
            style={{ marginBottom: '16px' }}
            extra={
              <Button 
                size="small" 
                icon={<CopyOutlined />}
                onClick={() => {
                  const apiKey = testForm.getFieldValue('api_key') || '[YOUR_API_KEY]';
                  const documentType = testForm.getFieldValue('document_type');
                  const storeImage = testForm.getFieldValue('store_image') || false;
                  const commandToCopy = generateCurlCommand(apiKey, documentType, storeImage);
                  navigator.clipboard.writeText(commandToCopy);
                  message.success('cURL command copied to clipboard');
                }}
              >
                Copy
              </Button>
            }
          >
            <pre style={{
              background: '#f6f8fa',
              border: '1px solid #e1e4e8',
              borderRadius: '4px',
              padding: '12px',
              fontSize: '12px',
              overflow: 'auto',
              margin: 0,
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
            }}>
              {(() => {
                const apiKey = testForm.getFieldValue('api_key') || '[YOUR_API_KEY]';
                const documentType = testForm.getFieldValue('document_type');
                const storeImage = testForm.getFieldValue('store_image') || false;
                return generateCurlCommand(apiKey, documentType, storeImage);
              })()}
            </pre>
          </Card>

          {/* Test Results */}
          {testResult && (
            <div style={{ marginTop: '24px' }}>
              <Title level={4}>Test Results</Title>
              
              {testResult.success ? (
                <Alert
                  message="Test Successful"
                  description={`Status: ${testResult.status} ${testResult.statusText}`}
                  type="success"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              ) : (
                <Alert
                  message="Test Failed"
                  description={`Status: ${testResult.status} ${testResult.statusText}`}
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}

              <Card 
                title="Response Data" 
                size="small"
                style={{ marginTop: '16px' }}
                bodyStyle={{ padding: '12px' }}
              >
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '300px',
                  margin: 0
                }}>
                  {JSON.stringify(testResult.success ? testResult.data : testResult.error, null, 2)}
                </pre>
              </Card>
            </div>
          )}

          {testLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>Processing OCR request...</div>
            </div>
          )}
        </Modal>

        {/* Settings Modal */}
        <Modal
          title="Application Settings"
          open={settingsModal}
          onCancel={() => setSettingsModal(false)}
          footer={null}
          width={600}
        >
          <Form
            form={settingsForm}
            layout="vertical"
            onFinish={handleUpdateApplication}
          >
            <Form.Item
              name="name"
              label="Application Name"
              rules={[{ required: true, message: 'Please enter application name' }]}
            >
              <Input placeholder="Enter application name" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={3} placeholder="Application description" />
            </Form.Item>

            <Form.Item
              name="quota_limit"
              label="Quota Limit"
              rules={[{ required: true, message: 'Please enter quota limit' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Monthly request quota"
                min={1}
              />
            </Form.Item>

            <Form.Item
              name="rate_limit_per_minute"
              label="Rate Limit (requests per minute)"
              rules={[{ required: true, message: 'Please enter rate limit' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Requests per minute"
                min={1}
              />
            </Form.Item>

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setSettingsModal(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit">Update Application</Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* Status Change Confirmation Modal */}
        <Modal
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              Confirm Status Change
            </Space>
          }
          open={statusModalVisible}
          onOk={handleConfirmStatusChange}
          onCancel={() => setStatusModalVisible(false)}
          confirmLoading={statusLoading}
          okText="Confirm"
          cancelText="Cancel"
          width={500}
        >
          {selectedApp && (
            <div>
              <Text>
                Are you sure you want to{' '}
                <Text strong style={{ color: selectedApp.status === 'active' ? '#ff4d4f' : '#52c41a' }}>
                  {selectedApp.status === 'active' ? 'DEACTIVATE' : 'ACTIVATE'}
                </Text>{' '}
                this application?
              </Text>
              
              <Card size="small" style={{ marginTop: '16px', backgroundColor: '#f5f5f5' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary">Application: </Text>
                    <Text strong>{selectedApp.name}</Text>
                  </div>
                  <div>
                    <Text type="secondary">Description: </Text>
                    <Text>{selectedApp.description}</Text>
                  </div>
                  <div>
                    <Text type="secondary">Current Status: </Text>
                    <Tag color={selectedApp.status === 'active' ? 'green' : 'red'}>
                      {selectedApp.status?.toUpperCase()}
                    </Tag>
                  </div>
                  <div>
                    <Text type="secondary">New Status: </Text>
                    <Tag color={selectedApp.status === 'active' ? 'red' : 'green'}>
                      {selectedApp.status === 'active' ? 'INACTIVE' : 'ACTIVE'}
                    </Tag>
                  </div>
                </Space>
              </Card>

              {selectedApp.status === 'active' && (
                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '4px', border: '1px solid #ffd666' }}>
                  <Text type="warning" strong>
                    Warning:
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                    Deactivating this application will immediately block all API calls using its API keys. 
                    Any ongoing integrations will stop working until you reactivate the application.
                  </Text>
                </div>
              )}
            </div>
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default APIManagement;
